import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { formatBytes } from "../certificateDownloadUtils";

const PHASE_LABEL = {
  pending: "Chờ xử lý",
  preparing: "Đang xử lý trên server",
  downloading: "Đang tải file",
  saving: "Đang lưu file",
  success: "Hoàn tất",
  error: "Thất bại",
};

function JobIcon({ phase }) {
  if (phase === "success") {
    return (
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
        <CheckCircle2 size={16} />
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
        <XCircle size={16} />
      </div>
    );
  }
  return (
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
      <Loader2 size={16} className="animate-spin" />
    </div>
  );
}

function JobProgressBar({ job }) {
  return (
    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
      {job.progress != null ? (
        <motion.div
          className={`h-full rounded-full ${
            job.phase === "success"
              ? "bg-emerald-500"
              : job.phase === "error"
              ? "bg-red-500"
              : "bg-blue-600"
          }`}
          animate={{ width: `${job.progress}%` }}
          transition={{ ease: "easeOut", duration: 0.2 }}
        />
      ) : (
        <motion.div
          className={`h-full w-1/2 rounded-full ${
            job.phase === "error" ? "bg-red-500" : "bg-blue-600"
          }`}
          animate={{ x: ["-110%", "210%"] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </div>
  );
}

function JobDetailText({ job }) {
  if (job.phase === "downloading") {
    if (job.progress != null) {
      return `Đã tải ${formatBytes(job.loadedBytes)} / ${formatBytes(job.totalBytes)}.`;
    }
    return `Đã nhận ${formatBytes(job.loadedBytes)} dữ liệu...`;
  }
  if (job.phase === "preparing") {
    return "Hệ thống đang render file trên máy chủ. Vui lòng chờ trong giây lát.";
  }
  if (job.phase === "saving") {
    return "Đang lưu file về máy của bạn...";
  }
  if (job.phase === "pending") {
    return "Đang chờ các tác vụ trước hoàn tất.";
  }
  if (job.phase === "success") {
    return job.fileName ? `Đã lưu ${job.fileName}.` : "Tải file thành công.";
  }
  if (job.phase === "error") {
    return job.error || "Không thể hoàn tất tác vụ tải.";
  }
  return "";
}

function JobRow({ job, compact = false }) {
  return (
    <div className={compact ? "rounded-xl border border-gray-100 bg-gray-50/80 p-3" : ""}>
      <div className="flex items-start gap-2.5">
        <JobIcon phase={job.phase} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-gray-800">{job.title}</p>
              {job.subtitle ? (
                <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">{job.subtitle}</p>
              ) : null}
            </div>
            {job.progress != null && job.phase !== "error" ? (
              <span className="shrink-0 text-[11px] font-black text-blue-600">{job.progress}%</span>
            ) : null}
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-gray-600">
            <span className="font-bold text-gray-700">{PHASE_LABEL[job.phase] || job.phase}</span>
            {compact ? ` — ${JobDetailText({ job })}` : null}
          </p>
          {!compact ? (
            <p className="mt-1 text-[11px] leading-relaxed text-gray-500">{JobDetailText({ job })}</p>
          ) : null}
          <JobProgressBar job={job} />
        </div>
      </div>
    </div>
  );
}

const CertificateDownloadQueuePanel = ({ jobs, onDismiss, title = "Hàng chờ tải file" }) => {
  if (!jobs?.length) return null;

  const activeJob =
    jobs.find((job) => !["success", "error"].includes(job.phase)) || jobs[jobs.length - 1];
  const successCount = jobs.filter((job) => job.phase === "success").length;
  const errorCount = jobs.filter((job) => job.phase === "error").length;
  const pendingCount = jobs.filter((job) => job.phase === "pending").length;
  const canDismiss = !jobs.some((job) => ["pending", "preparing", "downloading", "saving"].includes(job.phase));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.18 }}
        className="fixed bottom-5 right-5 z-[280] w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-200 bg-white/95 shadow-2xl backdrop-blur-sm"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <p className="text-sm font-black text-gray-800">{title}</p>
            <p className="mt-0.5 text-[11px] font-medium text-gray-500">
              {successCount}/{jobs.length} hoàn tất
              {pendingCount > 0 ? ` • ${pendingCount} đang chờ` : ""}
              {errorCount > 0 ? ` • ${errorCount} lỗi` : ""}
            </p>
          </div>
          {canDismiss && onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Đóng"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        <div className="space-y-3 p-4">
          <JobRow job={activeJob} />

          {jobs.length > 1 ? (
            <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
              {jobs
                .filter((job) => job.id !== activeJob.id)
                .map((job) => (
                  <JobRow key={job.id} job={job} compact />
                ))}
            </div>
          ) : null}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CertificateDownloadQueuePanel;
